import {
  ArrowRightIcon,
  BlueTickIcon,
  CardWithCrossIcon,
  GreyStrokeInfoIcon,
  OutlinedInfoIcon,
  WarningIcon
} from 'assets/icons';
import { CustomCheckbox } from 'components';
import {
  selectCurrentTeamFeePermissions,
  selectInvoiceLimit,
  selectMerchantCustomizations
} from 'containers/app/appSlice';
import { CleverTapEventsPaymentMethods } from 'containers/invoices/events';
import { InvoiceLevelPaymentModes } from 'containers/invoices/InvoicesSlice';
import { getMaximumAvailableAmountForTeam } from 'containers/invoices/utils';
import { PaymentMode } from 'containers/settings/settings.model';
import { PaymentSubCategoryTypes } from 'containers/settings/type';
import { useAppSelector } from 'hooks';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { logAnalyticEvent } from 'utils/analytics';

interface InvoicePaymentMethodsSectionProps {
  paymentModes: InvoiceLevelPaymentModes[];
  invoiceTotal: number;
  onUpdateInvoicePaymentModes: (updatedPaymentModes: InvoiceLevelPaymentModes[]) => void;
  noPaymentMethods: boolean;
  availablePaymentModes: PaymentMode[];
  isInitialisingPaymentModes?: boolean;
  isEdit?: boolean;
  partialPaymentEnabled: boolean;
}

const InvoicePaymentMethodsSection: FC<InvoicePaymentMethodsSectionProps> = ({
  paymentModes,
  invoiceTotal,
  onUpdateInvoicePaymentModes,
  noPaymentMethods,
  availablePaymentModes,
  isInitialisingPaymentModes = false,
  partialPaymentEnabled
}) => {
  const paymentModesPermissions = useAppSelector(selectCurrentTeamFeePermissions); // same as tips permissions
  const navigate = useNavigate();
  const hasSelfCheckoutPaymentModes = paymentModes?.some(paymentMode => paymentMode.teamLevelVisiblity) ?? false;
  const { invoiceMinLimit } = useAppSelector(selectInvoiceLimit);
  const { paymentModes: teamPaymentModes } = useAppSelector(selectMerchantCustomizations);

  const maximumAvailableAmount = useMemo(() => {
    return getMaximumAvailableAmountForTeam(teamPaymentModes);
  }, [teamPaymentModes]);

  const paymentModesRef = useRef(paymentModes);
  useEffect(() => {
    paymentModesRef.current = paymentModes;
  }, [paymentModes]);

  const mapPaymentModeForAnalytics = (paymentMode: InvoiceLevelPaymentModes | PaymentMode) => ({
    paymentMethodName: paymentMode.name,
    paymentMethodEnabled: paymentMode.enabled,
    paymentMethodAdminEnabled: paymentMode.adminEnabled,
    paymentMethodTeamLevelVisibility: paymentMode.teamLevelVisiblity
  });

  useEffect(() => {
    logAnalyticEvent(CleverTapEventsPaymentMethods.webPaymentMethodsPaymentMethodSection, {
      invoiceTotal: invoiceTotal,
      hasSelfCheckoutPaymentModes: hasSelfCheckoutPaymentModes,
      availablePaymentModes: JSON.stringify(availablePaymentModes.map(mapPaymentModeForAnalytics))
    });
  }, []);

  const isPaymentMethodSelectedByDefault = (paymentMode: InvoiceLevelPaymentModes, isValid: boolean) => {
    if (paymentMode.subCategory?.type === PaymentSubCategoryTypes.INSTALLMENT || paymentMode.name === 'ach') {
      if (paymentMode.isSelected && isValid) {
        return true;
      }
      return false;
    }
    if ((paymentMode.isSelected || paymentMode.invoiceLevelEnableStatus) && isValid) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isInitialisingPaymentModes) {
      return;
    }
    const currentPaymentModes = paymentModesRef.current;
    const updatedPaymentModes = currentPaymentModes.map(paymentMode => {
      const isValid =
        +invoiceTotal && +invoiceTotal >= +paymentMode.minimumAmount && +invoiceTotal <= +paymentMode.maximumAmount;
      const isValidPartialPayment = +invoiceTotal && +invoiceTotal >= +invoiceMinLimit && partialPaymentEnabled;
      const isCurrentlyValid = isValid || isValidPartialPayment;
      const isSelectedByDefault = isPaymentMethodSelectedByDefault(paymentMode, isCurrentlyValid);
      return {
        ...paymentMode,
        isValidForAmount: isValid,
        isSelected: isSelectedByDefault
      };
    });
    onUpdateInvoicePaymentModes(updatedPaymentModes);
  }, [invoiceTotal, isInitialisingPaymentModes, partialPaymentEnabled, invoiceMinLimit]);

  const onTogglePaymentMode = paymentMode => {
    const updatedPaymentModes = paymentModes.map(item => {
      return item.id === paymentMode.id ? { ...item, isSelected: !item.isSelected } : item;
    });
    onUpdateInvoicePaymentModes(updatedPaymentModes);
  };

  const routeToPaymentSettings = () => {
    navigate(routesPath.PAYMENT_SETTINGS);
  };

  const renderPaymentSettingsLink = () => (
    <div
      className="ml-auto flex shrink-0 items-center gap-1 text-sbase font-semibold text-primaryText"
      onClick={routeToPaymentSettings}
      role="button"
      tabIndex={0}
      aria-label="Navigate to payment method settings"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          routeToPaymentSettings();
        }
      }}>
      View or update in <span className="text-sbase font-semibold text-secondary">payment method settings</span>
      <ArrowRightIcon className="mt-1" />
    </div>
  );

  const shouldDisablePaymentMode = (paymentMode: InvoiceLevelPaymentModes) => {
    if (!paymentMode.isValidForAmount) {
      if (partialPaymentEnabled && invoiceTotal > 0) {
        return false;
      }
      return true;
    }
    return false;
  };

  const shouldShowPaymentModeAvailableAmountAlert = (paymentMode: InvoiceLevelPaymentModes) => {
    return invoiceTotal > 0 && !paymentMode.isValidForAmount && !partialPaymentEnabled;
  };

  const shouldShowPartialPaymentAlert = (paymentMode: InvoiceLevelPaymentModes) => {
    return invoiceTotal > 0 && !paymentMode.isValidForAmount && partialPaymentEnabled;
  };

  return (
    <div className="w-full">
      <div className="text-17px font-semibold text-heading">Payment methods</div>
      <div className="mt-1 text-sbase text-heading">
        Choose payment methods you want to display to the customer when paying for this invoice.
      </div>

      {invoiceTotal > +maximumAvailableAmount && !partialPaymentEnabled && (
        <div className="mt-3 flex items-center gap-1 rounded border border-secondary bg-[#B9D0FF4D] p-1.5">
          <OutlinedInfoIcon className="h-4 w-4 shrink-0 text-secondary" />
          <div className="text-sbase text-primaryText">
            Invoice exceeds all payment method limits - turn on partial payments to let customers pay online in smaller
            amounts.
          </div>
        </div>
      )}

      {noPaymentMethods && (
        <div className="mt-3 flex items-center gap-3.5 rounded-md border border-borderGray p-5">
          <div className="shrink-0">
            <CardWithCrossIcon />
          </div>
          <div>
            <div className="text-17px font-semibold text-error">
              {paymentModesPermissions.editTipsSettings
                ? 'No payment methods available'
                : 'No payment methods are enabled'}
            </div>
            {!paymentModesPermissions.editTipsSettings && (
              <div className="mt-2 text-sbase text-primaryText">Please reach out to your team owner or admin.</div>
            )}
          </div>
          {paymentModesPermissions.editTipsSettings && renderPaymentSettingsLink()}
        </div>
      )}
      {!noPaymentMethods && !hasSelfCheckoutPaymentModes && (
        <div className="mt-3 flex items-center gap-3.5 rounded-md border border-borderGray p-5">
          <div>
            <div className="text-17px font-semibold text-accentText">Self-checkout not enabled</div>
            {!paymentModesPermissions.editTipsSettings && (
              <div className="mt-2 text-sbase text-primaryText">
                Please reach out to your team owner or admin to enable self checkout.
              </div>
            )}
          </div>
          {paymentModesPermissions.editTipsSettings && renderPaymentSettingsLink()}
        </div>
      )}
      {hasSelfCheckoutPaymentModes && (
        <div className="mt-3 space-y-4 rounded border border-borderGray px-4 py-3">
          {paymentModes.map(
            paymentMode =>
              paymentMode.teamLevelVisiblity && (
                <div
                  className={'flex items-start border-b border-borderGray pb-3 last:border-none last:pb-0'}
                  key={paymentMode.id}>
                  <div className="mr-3.5 shrink-0">
                    <img src={paymentMode.icon.url} alt="" className="h-8 w-8" />
                  </div>
                  <div>
                    <div
                      className={`text-17px font-semibold text-tertiaryText ${
                        shouldDisablePaymentMode(paymentMode) ? 'pointer-events-none opacity-60' : ''
                      }`}>
                      {paymentMode.label}
                    </div>
                    {shouldShowPaymentModeAvailableAmountAlert(paymentMode) && (
                      <div className="mt-1 flex items-center gap-1">
                        <GreyStrokeInfoIcon className="shrink-0" width={15} height={15} />
                        <div className="text-px13 text-primaryText">
                          {paymentMode.label} is only available on transactions between{' '}
                          {getAmountWithCurrency(paymentMode.minimumAmount)} -{' '}
                          {getAmountWithCurrency(paymentMode.maximumAmount)}
                        </div>
                      </div>
                    )}
                    {shouldShowPartialPaymentAlert(paymentMode) && (
                      <div className="mt-1 flex items-center gap-1">
                        <WarningIcon
                          className="path-stroke-current shrink-0 text-pending-orange"
                          width={14}
                          height={14}
                        />
                        <div className="text-px13 text-primaryText">
                          Only available on partial amounts between &nbsp;
                          {getAmountWithCurrency(paymentMode.minimumAmount)} -{' '}
                          {getAmountWithCurrency(paymentMode.maximumAmount)}.
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className={`ml-auto ${
                      shouldDisablePaymentMode(paymentMode) ? 'pointer-events-none opacity-60' : ''
                    }`}>
                    <CustomCheckbox
                      id={paymentMode.id + paymentMode.name}
                      isChecked={paymentMode.isSelected}
                      onClick={() => onTogglePaymentMode(paymentMode)}>
                      {paymentMode.isSelected && <BlueTickIcon />}
                    </CustomCheckbox>
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicePaymentMethodsSection;
